import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics.service';
import { KpiQueryParams } from '../../types/kpi.types';
import { Loader } from '../Loader';

interface DaysToFirstTransferAnalysisProps {
  queryParams: KpiQueryParams;
}

interface AnalysisRow {
  daysToFirstTransfer: number;
  daysLabel: string;
  userCount: number;
  avgTransfersAt30Days: number;
  avgTransfersAt60Days: number;
  avgTransfersAt90Days: number;
  avgTransfersPerMonth: number;
  medianTransfersAt30Days: number;
  medianTransfersAt60Days: number;
  medianTransfersAt90Days: number;
  usersWithSecondTransfer30d: number;
  usersWithSecondTransfer31to60d: number;
  usersWithSecondTransfer61to90d: number;
  usersWithOnlyOneTransfer: number;
  secondTransferRate30d: number;
  secondTransferRate31to60d: number;
  secondTransferRate61to90d: number;
  onlyOneTransferRate: number;
  medianDaysToSecondTransfer: number | null;
  usersWithShiftsLast60d: number;
  churnedUsersWithShifts: number;
  churnedPercentage: number;
}

const DaysToFirstTransferAnalysis: React.FC<DaysToFirstTransferAnalysisProps> = ({
  queryParams,
}) => {
  const [data, setData] = useState<AnalysisRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await analyticsService.getDaysToFirstTransferEngagementAnalysis({
          database: queryParams.database,
          // Don't pass companyId - we want all companies combined
        });
        setData(result);
      } catch (err: any) {
        setError(err.message || 'Failed to load analysis data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [queryParams.database]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#d32f2f' }}>
        Error: {error}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={{ padding: '20px', color: '#666' }}>
        No data available for analysis.
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 600, color: '#333' }}>
          Days to First Transfer - Engagement Analysis
        </h2>
        <p style={{ margin: '0 0 24px 0', color: '#666', fontSize: '14px' }}>
          Analysis of how days to first transfer (1-30 days) correlates with future engagement metrics.
          This analysis includes all companies combined.
        </p>

        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6' }}>
                  Days to<br/>First Transfer (x)
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6' }}>
                  User<br/>Count
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#e3f2fd' }}>
                  Avg Transfers<br/>@ 30 Days
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#e3f2fd' }}>
                  Avg Transfers<br/>@ 60 Days
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#e3f2fd' }}>
                  Avg Transfers<br/>@ 90 Days
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#fff3e0' }}>
                  Avg Transfers<br/>Per Month
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#f3e5f5' }}>
                  % Made 2nd<br/>Transfer @ 30d
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#f3e5f5' }}>
                  % Made 2nd<br/>Transfer 31-60d
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#f3e5f5' }}>
                  % Made 2nd<br/>Transfer 61-90d
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#e8f5e9' }}>
                  Median Days<br/>to 2nd Transfer
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6', backgroundColor: '#ffebee' }}>
                  % Made Only<br/>1 Transfer Total
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#333', backgroundColor: '#fce4ec' }}>
                  Payro Churned<br/>% (90+ days)
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr
                  key={row.daysToFirstTransfer}
                  style={{
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa',
                  }}
                >
                  <td style={{ padding: '12px', fontWeight: 600, color: '#333', borderRight: '1px solid #dee2e6' }}>
                    {row.daysLabel}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6' }}>
                    {row.userCount.toLocaleString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#e3f2fd' }}>
                    {row.avgTransfersAt30Days.toFixed(2)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#e3f2fd' }}>
                    {row.avgTransfersAt60Days.toFixed(2)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#e3f2fd' }}>
                    {row.avgTransfersAt90Days.toFixed(2)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#fff3e0', fontWeight: 600 }}>
                    {row.avgTransfersPerMonth.toFixed(2)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#f3e5f5' }}>
                    {row.secondTransferRate30d.toFixed(1)}%
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#f3e5f5' }}>
                    {row.secondTransferRate31to60d.toFixed(1)}%
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#f3e5f5' }}>
                    {row.secondTransferRate61to90d.toFixed(1)}%
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#e8f5e9', fontWeight: 600 }}>
                    {row.medianDaysToSecondTransfer !== null ? row.medianDaysToSecondTransfer.toFixed(1) : 'N/A'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', borderRight: '1px solid #dee2e6', backgroundColor: '#ffebee', fontWeight: 600 }}>
                    {row.onlyOneTransferRate.toFixed(1)}%
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#555', backgroundColor: '#fce4ec', fontWeight: 600 }}>
                    {row.churnedPercentage.toFixed(1)}%<br/>
                    <span style={{ fontSize: '11px', color: '#999' }}>({row.churnedUsersWithShifts}/{row.usersWithShiftsLast60d})</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600, color: '#333' }}>
            📊 How to Read This Table
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#666', fontSize: '13px', lineHeight: '1.6' }}>
            <li><strong>Days to First Transfer:</strong> Number of days from user registration to their first successful transfer</li>
            <li><strong>Avg Transfers @ 30/60/90 Days:</strong> Average number of transfers users made within 30/60/90 days after their first transfer</li>
            <li><strong>Avg Transfers Per Month:</strong> Average monthly transfer rate for users in this segment (calculated from total transfers / months active)</li>
            <li><strong>% Made 2nd Transfer @ 30d:</strong> Percentage of users who made their 2nd transfer within 30 days of their first transfer (cumulative)</li>
            <li><strong>% Made 2nd Transfer 31-60d:</strong> Percentage of users who made their 2nd transfer between days 31-60 (non-cumulative, only if they didn't make it in first 30 days)</li>
            <li><strong>% Made 2nd Transfer 61-90d:</strong> Percentage of users who made their 2nd transfer between days 61-90 (non-cumulative, only if they didn't make it in first 60 days)</li>
            <li><strong>Median Days to 2nd Transfer:</strong> Median number of days between first and second transfer for users who made at least 2 transfers</li>
            <li><strong>% Made Only 1 Transfer Total:</strong> Percentage of users who made exactly 1 transfer in their lifetime (never made a second transfer)</li>
            <li><strong>Payro Churned %:</strong> Percentage of users (with shifts in last 60 days) who haven't transferred in 90+ days. Only counts users who are still active employees (had shifts in last 60 days).</li>
          </ul>
          <p style={{ margin: '12px 0 0 0', color: '#666', fontSize: '13px', fontStyle: 'italic' }}>
            💡 <strong>Goal:</strong> Identify the threshold (X days) where engagement metrics drop significantly. 
            Users who take longer than X days to make their first transfer show lower future engagement.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DaysToFirstTransferAnalysis;

